import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

const MAX_ATTEMPTS = 6;

export default function OrderSuccess() {
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const [order, setOrder] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("success");
          clear();
          return;
        }
        if (data.status === "expired") { setState("error"); return; }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) { setState("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [clear]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5 pt-28 pb-16">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-lg text-center">
        {state === "checking" && (
          <div data-testid="order-checking">
            <Loader2 size={48} className="mx-auto text-[#E6DFD4] animate-spin" />
            <h1 className="font-display text-4xl uppercase text-[#F5F5F5] mt-6">Confirming Payment</h1>
            <p className="mt-3 text-sm text-[#A3A3A3]">Hold tight, verifying your transaction…</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="order-success">
            <CheckCircle2 size={56} className="mx-auto text-[#E6DFD4]" />
            <h1 className="font-display text-5xl uppercase text-[#F5F5F5] mt-6">Order Confirmed</h1>
            <p className="mt-3 text-[#A3A3A3]">Thank you. Your peptides are being prepared for 48H UAE delivery.</p>
            {order && (
              <div className="mt-8 border border-[#1c1c1c] bg-[#0A0A0A] p-6 text-left">
                <div className="text-xs tracking-[0.15em] uppercase text-[#737373] mb-3">{order.order_id}</div>
                {order.items.map((i) => (
                  <div key={i.product_id} className="flex justify-between text-sm text-[#C9C4BB] py-1">
                    <span>{i.name} ×{i.quantity}</span><span>AED {i.price * i.quantity}</span>
                  </div>
                ))}
                <div className="flex justify-between font-display text-2xl text-[#E6DFD4] mt-3 pt-3 border-t border-[#1c1c1c]"><span>Total</span><span>AED {order.total}</span></div>
              </div>
            )}
            <div className="mt-8 flex gap-3 justify-center">
              <button onClick={() => navigate("/account")} className="bg-[#E6DFD4] text-black px-7 py-3.5 text-xs tracking-[0.2em] uppercase font-bold hover:bg-white">View Orders</button>
              <button onClick={() => navigate("/")} className="border border-[#2c2c2c] text-[#E6DFD4] px-7 py-3.5 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#111]">Keep Shopping</button>
            </div>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="order-pending">
            <Loader2 size={48} className="mx-auto text-[#E6DFD4]" />
            <h1 className="font-display text-4xl uppercase text-[#F5F5F5] mt-6">Payment Processing</h1>
            <p className="mt-3 text-sm text-[#A3A3A3]">This is taking longer than usual. Check your account shortly for confirmation.</p>
            <button onClick={() => navigate("/account")} className="mt-6 bg-[#E6DFD4] text-black px-7 py-3.5 text-xs tracking-[0.2em] uppercase font-bold">Go to Account</button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="order-error">
            <XCircle size={48} className="mx-auto text-red-400" />
            <h1 className="font-display text-4xl uppercase text-[#F5F5F5] mt-6">Something Went Wrong</h1>
            <p className="mt-3 text-sm text-[#A3A3A3]">We couldn&apos;t confirm this payment. If you were charged, contact us on WhatsApp.</p>
            <button onClick={() => navigate("/cart")} className="mt-6 bg-[#E6DFD4] text-black px-7 py-3.5 text-xs tracking-[0.2em] uppercase font-bold">Back to Cart</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
